import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { MaterialRow, SupplierMaterialRow, SupplierMaterialSaveRequest } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../session';

const PAGE_SIZE = 50;

interface MaterialDraft extends SupplierMaterialSaveRequest {
  materialName?: string;
}

/**
 * 平台物资快搜：对码写错不会报错，但会让判源/供货/结算归一不到同一个物资，
 * 所以不给供方手填 ID，改为按名称/编码搜索选择。
 * 编辑态物资不可改（后端按 supplier_id + material_id 定位），直接用已加载的名称回显。
 */
function MaterialPicker({
  value,
  onChange,
  disabled,
  fallbackLabel,
  onPick
}: {
  value?: string;
  onChange?: (value?: string) => void;
  disabled?: boolean;
  fallbackLabel?: string;
  onPick?: (material?: MaterialRow) => void;
}) {
  const [keyword, setKeyword] = useState('');
  const options = useQuery({
    queryKey: ['material-picker', keyword],
    queryFn: () => hq.material.list({ keyword: keyword || undefined, size: 20 }),
    enabled: !disabled
  });
  const rows = options.data?.items ?? [];
  const items = rows.map((row) => ({
    value: row.id,
    label: `${row.materialName ?? ''}（${row.materialCode ?? ''}）`
  }));
  return (
    <Select
      showSearch
      disabled={disabled}
      value={value}
      placeholder="搜索物资名称 / 编码"
      filterOption={false}
      onSearch={setKeyword}
      onChange={(next) => {
        onChange?.(next);
        onPick?.(rows.find((row) => row.id === next));
      }}
      loading={options.isFetching}
      options={disabled && value ? [{ value, label: fallbackLabel ?? value }, ...items] : items}
    />
  );
}

/**
 * 商品备案：供应商自有 SKU 编码/条码/型号 ↔ 平台物资映射。
 * 平台按 materialId 归一（判源、供货、结算都用它），映射缺失会导致对码失败。
 */
export default function GoodsSupplier() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [editing, setEditing] = useState<SupplierMaterialRow | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<MaterialDraft>();

  const supplier = useQuery({
    queryKey: ['supplier', 'profile'],
    queryFn: () => hq.catalog.supplierMe()
  });
  const supplierId = supplier.data?.supplier?.id ?? '';

  const list = useQuery({
    queryKey: ['supplier', 'material', supplierId, keyword],
    placeholderData: keepPreviousData,
    queryFn: () => hq.catalog.supplierMaterials(supplierId, { size: PAGE_SIZE, keyword: keyword || undefined }),
    enabled: Boolean(supplierId)
  });

  const prices = useQuery({
    queryKey: ['supplier', 'price', supplierId],
    queryFn: () => hq.catalog.prices({ supplierId }),
    enabled: Boolean(supplierId)
  });

  const save = useMutation({
    mutationFn: (values: MaterialDraft) => hq.catalog.saveSupplierMaterial(supplierId, values),
    onSuccess: async () => {
      message.success('SKU 映射已保存');
      setOpen(false);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['supplier', 'material'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const remove = useMutation({
    mutationFn: (id: string) => hq.catalog.removeSupplierMaterial(id),
    onSuccess: async () => {
      message.success('已删除该映射');
      await queryClient.invalidateQueries({ queryKey: ['supplier', 'material'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败')
  });

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      materialId: '',
      materialName: '',
      supplierSkuCode: '',
      supplierSkuName: '',
      supplierBarcode: '',
      supplierModel: '',
      supplierSpec: '',
      originPlace: '',
      leadTimeHours: 24,
      moq: 1,
      packQty: 1,
      isPreferred: false,
      status: 'ACTIVE'
    });
    setOpen(true);
  };

  const openEdit = (row: SupplierMaterialRow) => {
    setEditing(row);
    form.setFieldsValue({
      materialId: row.materialId,
      materialName: row.materialName ?? '',
      supplierSkuCode: row.supplierSkuCode ?? '',
      supplierSkuName: row.supplierSkuName ?? '',
      supplierBarcode: row.supplierBarcode ?? '',
      supplierModel: row.supplierModel ?? '',
      supplierSpec: row.supplierSpec ?? '',
      originPlace: row.originPlace ?? '',
      leadTimeHours: row.leadTimeHours,
      moq: row.moq,
      packQty: row.packQty,
      isPreferred: row.isPreferred,
      status: row.status
    });
    setOpen(true);
  };

  const priceByMaterial = new Map<string, number[]>(
    Object.entries(
      (prices.data ?? []).reduce<Record<string, number[]>>((acc, row) => {
        (acc[row.materialId] ??= []).push(row.price);
        return acc;
      }, {})
    )
  );

  const columns: ColumnsType<SupplierMaterialRow> = [
    { title: '平台物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '平台物资名称', dataIndex: 'materialName', width: 200, ellipsis: true },
    { title: '规格', dataIndex: 'materialSpec', width: 140, render: (value: string | null) => value || '-' },
    { title: '我方 SKU', dataIndex: 'supplierSkuCode', width: 140, render: (value: string | null) => value || '-' },
    { title: '我方品名', dataIndex: 'supplierSkuName', width: 160, render: (value: string | null) => value || '-' },
    { title: '条码', dataIndex: 'supplierBarcode', width: 140, render: (value: string | null) => value || '-' },
    { title: '型号', dataIndex: 'supplierModel', width: 120, render: (value: string | null) => value || '-' },
    { title: '产地', dataIndex: 'originPlace', width: 110, render: (value: string | null) => value || '-' },
    {
      title: '交货时效',
      dataIndex: 'leadTimeHours',
      width: 110,
      align: 'right',
      render: (value: number) => `${value}h`
    },
    { title: '起订量', dataIndex: 'moq', width: 90, align: 'right' },
    { title: '包装量', dataIndex: 'packQty', width: 90, align: 'right' },
    {
      title: '协议价',
      key: 'price',
      width: 130,
      align: 'right',
      render: (_value, row) => {
        const prices4 = priceByMaterial.get(row.materialId) ?? [];
        if (prices4.length === 0) return <Typography.Text type="secondary">未报价</Typography.Text>;
        return formatAmount(Math.min(...prices4), 4);
      }
    },
    {
      title: '首选',
      dataIndex: 'isPreferred',
      width: 80,
      render: (value: boolean) => (value ? <Tag color="green">首选</Tag> : <Tag>备选</Tag>)
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value: string) => (value === 'ACTIVE' ? <Tag color="blue">生效</Tag> : <Tag>{value}</Tag>)
    },
    {
      title: '操作',
      key: 'action',
      width: 130,
      fixed: 'right',
      render: (_value, row) => (
        <Space size={4}>
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Button
            size="small"
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              modal.confirm({
                title: '删除 SKU 映射？',
                content: `${row.materialName ?? ''} 的映射删除后，平台侧对码与结算将缺少供方编码。`,
                okText: '删除',
                cancelText: '取消',
                onOk: () => remove.mutateAsync(row.id)
              })
            }
          />
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          商品备案
          <Typography.Text type="secondary">SKU 映射与价格协议，平台按物资主键归一</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Input.Search
            allowClear
            placeholder="物资编码 / 名称"
            style={{ width: 200 }}
            onSearch={(value) => setKeyword(value)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} loading={list.isFetching}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} disabled={!supplierId} onClick={openCreate}>
            新增映射
          </Button>
        </Space>
      }
    >
      {supplier.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="供应商档案加载失败"
          description={
            supplier.error instanceof ApiError ? supplier.error.friendlyMessage : String(supplier.error)
          }
        />
      ) : null}

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="SKU 映射加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<SupplierMaterialRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={list.data?.items ?? []}
        loading={Boolean(supplierId) && list.isPending}
        pagination={false}
        scroll={{ x: 1700 }}
        locale={{ emptyText: '尚未维护 SKU 映射，请点击「新增映射」建立与平台物资的对应关系' }}
      />

      <Modal
        title={editing ? '编辑 SKU 映射' : '新增 SKU 映射'}
        open={open}
        onCancel={() => setOpen(false)}
        okText="保存"
        cancelText="取消"
        confirmLoading={save.isPending}
        onOk={() => form.submit()}
        width={720}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="平台物资从目录中搜索选择，请对准院方下发的物资编码"
          description="同一供应商 + 物资只会保留一条映射，重复提交即为覆盖更新。"
        />
        <Form form={form} layout="vertical" onFinish={(values) => save.mutate(values)}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="materialId"
                label="平台物资"
                rules={[{ required: true, message: '请选择平台物资' }]}
              >
                <MaterialPicker
                  disabled={editing !== null}
                  fallbackLabel={`${editing?.materialName ?? ''}（${editing?.materialCode ?? ''}）`}
                  onPick={(material) => form.setFieldsValue({ materialName: material?.materialName ?? '' })}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="materialName" label="平台物资名称">
                <Input disabled placeholder="选定物资后自动回填" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierSkuCode" label="我方 SKU 编码">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierSkuName" label="我方品名">
                <Input maxLength={128} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierBarcode" label="条码">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierModel" label="型号">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierSpec" label="供方规格">
                <Input maxLength={128} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="originPlace" label="产地">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="leadTimeHours" label="交货时效（小时）">
                <InputNumber min={1} max={720} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="moq" label="起订量">
                <InputNumber min={0} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="packQty" label="包装量">
                <InputNumber min={1} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isPreferred" label="设为首选供应商" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="状态" tooltip="停用后该 SKU 不再参与判源与供货">
                <Select
                  options={[
                    { value: 'ACTIVE', label: '生效' },
                    { value: 'STOP', label: '停用' }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
}
